import {
  type PaywallFeatureStateFromEntitlement,
  setPaywallCacheFeatureData,
  updatePaywallFeatureState,
} from '@canvas/paywall';
import { captureException, ContentType } from '@canvas/sentry-react';

const DATABASE_PAYWALL_FEATURE_STATE_SESSION_KEY = 'database-paywall-feature-state';

// Some database create/recycle paths briefly load with tokens that do not include
// paywall features. Keep the last real payload for this tab so that missing data
// does not get interpreted as a locked AI entitlement.
let lastKnownPaywallFeatureState: PaywallFeatureStateFromEntitlement | undefined;
const capturedStorageErrorLocations = new Set<string>();

const isPaywallFeatureStateFromEntitlement = (
  value: unknown,
): value is PaywallFeatureStateFromEntitlement =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const captureDatabasePaywallFeatureStateCacheExceptionOnce = (
  error: unknown,
  captureLocation: string,
) => {
  if (capturedStorageErrorLocations.has(captureLocation)) {
    return;
  }

  capturedStorageErrorLocations.add(captureLocation);
  captureException(error, {
    tags: {
      captureLocation,
      contentType: ContentType.DATABASE,
    },
  });
};

export const getLastKnownDatabasePaywallFeatureState = ():
  | PaywallFeatureStateFromEntitlement
  | undefined => {
  if (lastKnownPaywallFeatureState) {
    return lastKnownPaywallFeatureState;
  }

  try {
    const serializedFeatureState = globalThis.sessionStorage?.getItem(
      DATABASE_PAYWALL_FEATURE_STATE_SESSION_KEY,
    );

    if (!serializedFeatureState) {
      return undefined;
    }

    const parsedFeatureState = JSON.parse(serializedFeatureState);

    if (!isPaywallFeatureStateFromEntitlement(parsedFeatureState)) {
      globalThis.sessionStorage?.removeItem(DATABASE_PAYWALL_FEATURE_STATE_SESSION_KEY);
      return undefined;
    }

    lastKnownPaywallFeatureState = parsedFeatureState;
    return lastKnownPaywallFeatureState;
  } catch (error) {
    captureDatabasePaywallFeatureStateCacheExceptionOnce(
      error,
      'get-last-known-database-paywall-feature-state',
    );
    return undefined;
  }
};

export const syncDatabasePaywallFeatureState = (
  featureState: PaywallFeatureStateFromEntitlement,
  {
    shouldWritePaywallCache = true,
  }: {
    shouldWritePaywallCache?: boolean;
  } = {},
) => {
  lastKnownPaywallFeatureState = featureState;

  // sessionStorage survives the database app being reloaded in the same tab, while
  // still avoiding a longer-lived entitlement copy across browser sessions.
  try {
    globalThis.sessionStorage?.setItem(
      DATABASE_PAYWALL_FEATURE_STATE_SESSION_KEY,
      JSON.stringify(featureState),
    );
  } catch (error) {
    captureDatabasePaywallFeatureStateCacheExceptionOnce(
      error,
      'sync-database-paywall-feature-state-session-storage',
    );
  }

  if (shouldWritePaywallCache) {
    // The shared paywall cache remains best-effort. The in-memory paywall store is
    // updated below even if persistence fails.
    void setPaywallCacheFeatureData(featureState).catch((error) => {
      captureDatabasePaywallFeatureStateCacheExceptionOnce(
        error,
        'sync-database-paywall-feature-state-paywall-cache',
      );
    });
  }

  updatePaywallFeatureState(featureState);
};

export const resetDatabasePaywallFeatureStateCacheForTests = () => {
  lastKnownPaywallFeatureState = undefined;
  capturedStorageErrorLocations.clear();
  globalThis.sessionStorage?.removeItem(DATABASE_PAYWALL_FEATURE_STATE_SESSION_KEY);
};
